import { lookupConnectorCompat } from "./signalCompatibility";

export function worstSeverity(notes) {
  if (notes.some((n) => n.severity === "error")) return "error";
  if (notes.some((n) => n.severity === "warn")) return "warn";
  return "info";
}

const DISALLOWED_POWER_CHAINS = {
  "Power Conditioner": ["Power Conditioner", "Surge Protector", "UPS"],
  "Surge Protector": ["Power Conditioner", "Surge Protector", "UPS"],
  UPS: ["Power Conditioner", "Surge Protector", "UPS"],
};

function findPowerConnections(device, nodes, edges) {
  const targetHandle = `power-in-${device.id}`;
  const powerEdges = edges.filter((e) => e.targetHandle === targetHandle);
  if (powerEdges.length === 0) return [];

  return powerEdges.map((edge) => {
    const sourceNode = nodes.find((n) => n.id === edge.source);
    if (!sourceNode) return { output: null, edgeId: edge.id, sourceNode: null, cable: null };

    const handleId = edge.sourceHandle;
    const match = handleId.match(/^power-out-(\d+)$/);
    if (!match) return { output: null, edgeId: edge.id, sourceNode, cable: null };

    const outputId = Number(match[1]);
    const outputs = sourceNode.data?.device?.power_outputs || [];
    const output = outputs.find((po) => po.id === outputId) || null;
    return {
      output,
      edgeId: edge.id,
      sourceNode,
      cable: edge.data?.cable || null,
      cableChain: edge.data?.cableChain || [],
      suppliedCable: edge.data?.suppliedCable || false,
    };
  });
}

function getEffectiveOutput(powerOutput, cable) {
  if (!cable) {
    return {
      connector: powerOutput.connector,
      polarity: powerOutput.polarity,
      voltage_options: powerOutput.voltage_options,
      current_ma: powerOutput.current_ma,
    };
  }
  switch (cable.cable_type) {
    case "standard":
      return {
        connector: cable.output_connector,
        polarity: powerOutput.polarity,
        voltage_options: powerOutput.voltage_options,
        current_ma: powerOutput.current_ma,
      };
    case "polarity_reverser":
      return {
        connector: cable.output_connector,
        polarity: cable.output_polarity,
        voltage_options: powerOutput.voltage_options,
        current_ma: powerOutput.current_ma,
      };
    case "voltage_converter":
      return {
        connector: cable.output_connector,
        polarity: cable.output_polarity,
        voltage_options: cable.output_voltage ? [cable.output_voltage] : [],
        current_ma: powerOutput.current_ma,
      };
    case "current_doubler":
      return {
        connector: cable.output_connector,
        polarity: cable.output_polarity,
        voltage_options: powerOutput.voltage_options,
        current_ma: powerOutput.current_ma * (cable.input_count || 1),
      };
    case "splitter":
      return {
        connector: cable.output_connector,
        polarity: cable.output_polarity,
        voltage_options: powerOutput.voltage_options,
        current_ma: powerOutput.current_ma,
      };
    default:
      return {
        connector: cable.output_connector,
        polarity: cable.output_polarity,
        voltage_options: powerOutput.voltage_options,
        current_ma: powerOutput.current_ma,
      };
  }
}

function deviceHasSignalPorts(device) {
  const ports = device.device_ports || [];
  return ports.length > 0;
}

function checkSinglePowerConnection(device, conn) {
  const { output: powerOutput, cable, cableChain } = conn;
  if (!powerOutput) return [];

  // Chain through all cables in sequence to get the effective output
  let effective;
  if (cableChain && cableChain.length > 0) {
    effective = getEffectiveOutput(powerOutput, cableChain[0].cable);
    for (let i = 1; i < cableChain.length; i++) {
      effective = getEffectiveOutput(effective, cableChain[i].cable);
    }
  } else {
    effective = getEffectiveOutput(powerOutput, cable);
  }
  return checkEffectiveOutput(device, effective, conn);
}

function checkEffectiveOutput(device, effective, conn) {
  const { output: powerOutput, sourceNode } = conn;
  const notes = [];

  if (conn.suppliedCable) return notes;

  if (effective.connector && device.power_connector) {
    const compat = lookupConnectorCompat(effective.connector, device.power_connector);
    if (compat.level === "error") {
      notes.push({
        message: `Connector mismatch: supply has ${effective.connector}, device needs ${device.power_connector}`,
        severity: "error",
      });
    }
  }

  if (powerOutput && powerOutput.outlet_plug_type && device.power_plug_type) {
    if (powerOutput.outlet_plug_type === "two_prong" && device.power_plug_type === "three_prong") {
      notes.push({
        message: "Plug incompatible: three-prong plug won't fit a two-prong outlet",
        severity: "error",
      });
    }
  }

  if (sourceNode) {
    const sourceDevice = sourceNode.data?.device;
    if (sourceDevice) {
      const sourceTypes = (sourceDevice.device_types || []).map((dt) => dt.name);
      const targetTypes = (device.device_types || []).map((dt) => dt.name);

      if (!sourceTypes.includes("Power Supply")) {
        let chainWarned = false;
        for (const sourceType of sourceTypes) {
          if (chainWarned) break;
          const disallowed = DISALLOWED_POWER_CHAINS[sourceType];
          if (!disallowed) continue;
          for (const targetType of targetTypes) {
            if (disallowed.includes(targetType)) {
              notes.push({
                message: `Unsafe power chain: ${sourceType} should not power a ${targetType}`,
                severity: "info",
              });
              chainWarned = true;
              break;
            }
          }
        }
      }
    }
  }

  if (device.power_polarity && effective.polarity &&
      effective.polarity !== "n_a" &&
      effective.polarity !== device.power_polarity) {
    notes.push({
      message: `Polarity mismatch: supply is ${effective.polarity}, device needs ${device.power_polarity}`,
      severity: "error",
    });
  }

  if (device.power_voltage_min != null && device.power_voltage_max != null &&
      effective.voltage_options && effective.voltage_options.length > 0) {
    const min = Number(device.power_voltage_min);
    const max = Number(device.power_voltage_max);
    const hasCompatible = effective.voltage_options.some((v) => {
      const volts = Number(v);
      return volts >= min && volts <= max;
    });
    if (!hasCompatible) {
      notes.push({
        message: `Voltage incompatible: supply offers ${effective.voltage_options.map(Number).join("/")}V, device needs ${min}\u2013${max}V`,
        severity: "error",
      });
    }
  }

  if (device.power_current_ma && effective.current_ma &&
      device.power_current_ma > effective.current_ma) {
    notes.push({
      message: `Current insufficient: device draws ${device.power_current_ma}mA, output provides ${effective.current_ma}mA`,
      severity: "error",
    });
  }

  return notes;
}

function rigCableRef(edge) {
  // Check cableChain first (new format), fall back to legacy fields
  const chain = edge.data?.cableChain;
  if (chain && chain.length > 0) {
    return chain[0].rigCableId || chain[0].rigCableTempId;
  }
  return edge.data?.rigCableId || edge.data?.rigCableTempId;
}

function allRigCableRefs(edge) {
  const chain = edge.data?.cableChain;
  if (chain && chain.length > 0) {
    return chain.map((entry) => entry.rigCableId || entry.rigCableTempId).filter(Boolean);
  }
  const ref = edge.data?.rigCableId || edge.data?.rigCableTempId;
  return ref ? [ref] : [];
}

const DOUBLER_TYPES = new Set(["current_doubler", "voltage_doubler"]);

export function checkCablePlugCounts(edges, rigCables) {
  const issues = [];
  if (!rigCables || rigCables.length === 0) return issues;

  // Group edges by rig_cable ref (an edge may reference multiple via chain)
  const edgesByRigCable = new Map();
  for (const rc of rigCables) {
    const ref = rc.id || rc.tempId;
    edgesByRigCable.set(ref, []);
  }
  for (const edge of edges) {
    for (const ref of allRigCableRefs(edge)) {
      if (edgesByRigCable.has(ref)) {
        edgesByRigCable.get(ref).push(edge);
      }
    }
  }

  for (const rc of rigCables) {
    const ref = rc.id || rc.tempId;
    const cable = rc.cable;
    if (!cable) continue;

    const connectedEdges = edgesByRigCable.get(ref);
    const edgeCount = connectedEdges.length;
    const maxPlugs = Math.max(cable.input_count || 1, cable.output_count || 1);

    // Over-assignment: more connections than plugs
    if (edgeCount > maxPlugs) {
      const note = {
        message: `Too many connections for this cable (${edgeCount} connected, max ${maxPlugs})`,
        severity: "error",
      };
      for (const edge of connectedEdges) {
        issues.push({ edgeId: edge.id, notes: [note] });
      }
      continue;
    }

    // Under-assignment: doublers require all inputs
    if (DOUBLER_TYPES.has(cable.cable_type) && edgeCount < (cable.input_count || 1)) {
      const note = {
        message: `This cable has ${cable.input_count} inputs but only ${edgeCount} connected — cable won't function`,
        severity: "error",
      };
      for (const edge of connectedEdges) {
        issues.push({ edgeId: edge.id, notes: [note] });
      }
      continue;
    }

    // Under-assignment: splitters with unused outputs (informational)
    if (cable.cable_type === "splitter" && edgeCount < (cable.output_count || 1)) {
      const unused = (cable.output_count || 1) - edgeCount;
      const note = {
        message: `Cable has ${cable.output_count} outputs, ${unused} unused`,
        severity: "info",
      };
      for (const edge of connectedEdges) {
        issues.push({ edgeId: edge.id, notes: [note] });
      }
    }
  }

  return issues;
}

function endTypesMate(endType1, endType2) {
  if (endType1 === "symmetrical" && endType2 === "symmetrical") return true;
  if (endType1 === "plug" && endType2 === "jack") return true;
  if (endType1 === "jack" && endType2 === "plug") return true;
  return false;
}

export function checkChainCompatibility(chain) {
  const notes = [];
  if (!chain || chain.length < 2) return notes;

  for (let i = 0; i < chain.length - 1; i++) {
    const prev = chain[i].cable;
    const next = chain[i + 1].cable;
    if (!prev || !next) continue;

    const outConn = prev.output_connector;
    const inConn = next.input_connector;
    const outEnd = prev.output_end_type || "plug";
    const inEnd = next.input_end_type || "plug";

    if (outConn && inConn && outConn !== inConn) {
      notes.push({
        message: `Chain step ${i + 1}→${i + 2}: connector mismatch (${outConn} → ${inConn})`,
        severity: "error",
      });
    } else if (!endTypesMate(outEnd, inEnd)) {
      notes.push({
        message: `Chain step ${i + 1}→${i + 2}: end type mismatch (${outEnd} → ${inEnd})`,
        severity: "error",
      });
    }
  }

  return notes;
}

export function findAutoGroupRigCable(newEdge, existingEdges, rigCables) {
  if (newEdge.data?.connectionType !== "power") return null;
  if (!rigCables || rigCables.length === 0) return null;

  // Check target device: other edges to the same power input with an available rig_cable
  const sameTargetEdges = existingEdges.filter(
    (e) => e.target === newEdge.target && e.targetHandle === newEdge.targetHandle
  );

  const match = findAvailableRigCable(sameTargetEdges, existingEdges, rigCables);
  if (match) return match;

  // Check source output: other edges from the same power output with an available rig_cable
  const sameSourceEdges = existingEdges.filter(
    (e) => e.source === newEdge.source && e.sourceHandle === newEdge.sourceHandle
  );

  return findAvailableRigCable(sameSourceEdges, existingEdges, rigCables);
}

function findAvailableRigCable(candidateEdges, allEdges, rigCables) {
  for (const edge of candidateEdges) {
    const ref = rigCableRef(edge);
    if (!ref) continue;

    const rc = rigCables.find((r) => (r.id || r.tempId) === ref);
    if (!rc?.cable) continue;

    const cable = rc.cable;
    const maxPlugs = Math.max(cable.input_count || 1, cable.output_count || 1);
    const connectedCount = allEdges.filter((e) => rigCableRef(e) === ref).length;

    if (connectedCount < maxPlugs) return rc;
  }
  return null;
}

export function checkPowerIssues(nodes, edges) {
  const issues = [];

  for (const node of nodes) {
    const device = node.data?.device;
    if (!device) continue;

    const busPowerPort = (device.device_ports || []).find((p) => p.bus_power);
    const hasPowerConnector = device.power_connector && device.power_connector !== "none";

    if (!hasPowerConnector && !busPowerPort) continue;

    // Check if bus_power port is connected via a signal edge
    const busPowered = busPowerPort && edges.some(
      (e) => e.sourceHandle === String(busPowerPort.id) || e.targetHandle === String(busPowerPort.id),
    );

    // Skip traditional power checks if device is only bus-powered (no barrel connector)
    if (!hasPowerConnector) {
      if (!busPowered && deviceHasSignalPorts(device)) {
        issues.push({
          device: device.name,
          nodeId: node.id,
          edgeId: null,
          notes: [{ message: "Device needs USB connection for power", severity: "info" }],
        });
      }
      continue;
    }

    const powerConns = findPowerConnections(device, nodes, edges);

    if (powerConns.length === 0) {
      if (busPowered) continue; // Powered via USB, no traditional power needed
      if (deviceHasSignalPorts(device)) {
        issues.push({
          device: device.name,
          nodeId: node.id,
          edgeId: null,
          notes: [{ message: "No power connection", severity: "warn" }],
        });
      }
      continue;
    }

    for (const conn of powerConns) {
      const notes = checkSinglePowerConnection(device, conn);
      if (notes.length > 0) {
        issues.push({
          device: device.name,
          nodeId: node.id,
          edgeId: conn.edgeId || null,
          notes,
        });
      }
    }
  }

  // Check overcapacity per power output
  const outputUsage = {};
  for (const edge of edges) {
    const match = edge.sourceHandle?.match(/^power-out-(\d+)$/);
    if (!match) continue;
    const outputId = match[1];
    if (!outputUsage[outputId]) outputUsage[outputId] = { count: 0, edge };
    outputUsage[outputId].count += 1;
  }
  for (const [outputId, usage] of Object.entries(outputUsage)) {
    if (usage.count <= 1) continue;
    const sourceNode = nodes.find((n) => n.id === usage.edge.source);
    const sourceDevice = sourceNode?.data?.device;
    if (!sourceDevice) continue;
    const output = (sourceDevice.power_outputs || []).find((po) => po.id === Number(outputId));
    if (!output || !output.count || usage.count <= output.count) continue;
    issues.push({
      device: sourceDevice.name,
      nodeId: sourceNode.id,
      edgeId: null,
      notes: [{
        message: `${output.name} has ${usage.count} connections but only ${output.count} ${output.count === 1 ? "outlet" : "outlets"}`,
        severity: "info",
      }],
    });
  }

  return issues;
}
